import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box, Checkbox, FormControlLabel, FormHelperText, FormLabel,
  MenuItem, Radio, RadioGroup, Select, TextField,
  Typography, Divider, Alert, CircularProgress, FormControl, InputLabel,
  InputAdornment,
} from '@mui/material';
import { getFormSpecs, buildSchema } from '../../config/formsConfig';
import api from '../../api/api';
import AvailabilityPicker from './AvailabilityPicker';

const COMMON_KEYS = [
  'titulo','descripcion','acceso','urgencia','horarios_preferidos',
  'fotos','presupuesto_estimado','acepta_visita','acepta_presupuesto_previo','terminos_aceptados'
];

// Convierte un campo de la BD al formato que usa el componente Field
const parseOptions = (options) => {
  if (!options) return undefined;
  if (Array.isArray(options)) return options;
  try { return JSON.parse(options); } catch {
    return options.split(",").map(o => o.trim());
  }
};

// Los campos arrancan en '' (select, number, text). Para zod '' no es
// "ausente": un opcional con .optional() lo rechaza ('' no está en el enum;
// z.coerce.number('') da 0 y .positive() falla) y el envío se trababa con
// "Campo requerido" en campos sin asterisco. Vacío = no informado; los
// obligatorios siguen fallando igual. El presupuesto llega con coma decimal
// (lo formatea el campo de moneda) y coerce no la entiende.
const vaciosComoAusentes = (valores) => {
  if (!valores || typeof valores !== 'object' || Array.isArray(valores)) return valores;
  const out = {};
  for (const [k, v] of Object.entries(valores)) {
    if (v === '') continue;
    out[k] = k === 'ubicacion' ? vaciosComoAusentes(v) : v;
  }
  if (typeof out.presupuesto_estimado === 'string') {
    out.presupuesto_estimado = out.presupuesto_estimado.replace(',', '.');
  }
  return out;
};

// Urgencias apagadas para la prueba (backend config/urgenciasConfig.js,
// 27-10-2026): "Urgente (48h)" no se ofrece, venga de formsConfig o de la base.
// El backend igual guarda todo como presupuesto.
const OPCIONES_OCULTAS = { urgencia: ['Urgente (48h)'] };
const sinOcultas = (key, options) =>
  Array.isArray(options) && OPCIONES_OCULTAS[key]
    ? options.filter(o => !OPCIONES_OCULTAS[key].includes(o))
    : options;

const dbFieldToSpec = (f) => ({
  key:          f.field_key,
  type:         f.field_type,
  label:        f.label,
  required:     f.required,
  options:      parseOptions(f.options),
  maxFiles:     f.max_files,
  min:          f.min_value,
  max:          f.max_value,
  defaultValue: f.default_value,
});

// Ancho en % del contenedor para cada tipo de campo
const getWidth = (spec) => {
  if (['textarea','multiselect','radio','files','timeslots'].includes(spec.type)) return '100%';
  if (spec.type === 'select') return '16%';
  if (spec.type === 'number') return '20%';
  if (spec.type === 'date')   return '11%';
  if (spec.type === 'text')   return spec.key.includes('descripcion') ? '100%' : '11%';
  return '16%';
};

// ── Estilos forzados para inputs/selects — evita herencia del tema oscuro ──────
const inputSx = {
  backgroundColor: '#fff',
  borderRadius: '10px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    borderRadius: '10px',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(231,164,50,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#E7A432', borderWidth: 1.5 },
  },
  '& .MuiInputBase-input': {
    color: '#163244',
    backgroundColor: '#fff',
    '&::placeholder': { color: '#b0bec5', opacity: 1 },
  },
  '& .MuiInputLabel-root': { color: '#7a93a4', fontSize: '0.82rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#E7A432' },
  '& .MuiSelect-select': { color: '#163244', backgroundColor: '#fff' },
  '& .MuiFormHelperText-root': { fontSize: '0.72rem' },
};

// Props del dropdown del Select — fondo blanco forzado
const menuProps = {
  PaperProps: {
    sx: {
      backgroundColor: '#fff',
      borderRadius: '10px',
      border: '1px solid rgba(0,0,0,0.1)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      '& .MuiMenuItem-root': {
        color: '#163244',
        fontSize: '0.85rem',
        '&:hover': { backgroundColor: 'rgba(231,164,50,0.08)' },
        '&.Mui-selected': { backgroundColor: 'rgba(22,50,68,0.08)', color: '#163244' },
        '&.Mui-selected:hover': { backgroundColor: 'rgba(231,164,50,0.1)' },
      },
    },
  },
};

// Estilo común para el wrapper de cada campo
const fieldWrapper = (width) => ({
  width,
  minWidth: width === '100%' ? 0 : width === '16%' ? '160px' : width === '20%' ? '150px' : '120px',
  flexShrink: 0,
  flexGrow: width === '100%' ? 1 : 0,
  boxSizing: 'border-box',
  '@media (max-width:600px)': { width: '100%', minWidth: 0 },
});

function Field({ spec, control, errors }) {
  const err = errors?.[spec.key];
  const w = getWidth(spec);

  switch (spec.type) {

    case 'text':
    case 'number':
    case 'date':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => {
            const isCurrency = spec.key === 'presupuesto_estimado';
            const displayValue = isCurrency && field.value !== '' && field.value !== undefined
              ? (() => {
                  const [intPart, decPart] = String(field.value).split(',');
                  const formatted = new Intl.NumberFormat('es-AR').format(intPart.replace(/[^0-9]/g, '') || 0);
                  return decPart !== undefined ? formatted + ',' + decPart : formatted;
                })()
              : field.value;
            return (
              <TextField
                {...field}
                value={isCurrency ? (displayValue || '') : field.value}
                onChange={isCurrency ? (e) => {
                  let val = e.target.value.replace(/\./g, '').replace(/[^0-9,]/g, '');
                  const parts = val.split(',');
                  if (parts.length > 2) val = parts[0] + ',' + parts.slice(1).join('');
                  if (parts[1] !== undefined) val = parts[0] + ',' + parts[1].slice(0, 2);
                  field.onChange(val);
                } : field.onChange}
                label={spec.label}
                type={isCurrency ? 'text' : spec.type === 'number' ? 'number' : spec.type === 'date' ? 'date' : 'text'}
                inputMode={isCurrency ? 'decimal' : undefined}
                size="small" fullWidth
                required={spec.required}
                error={!!err}
                helperText={err ? 'Campo requerido o inválido' : ''}
                InputLabelProps={spec.type === 'date' ? { shrink: true } : undefined}
                InputProps={isCurrency ? {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                } : undefined}
                sx={inputSx}
              />
            );
          }} />
        </Box>
      );

    case 'textarea':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <TextField
              {...field}
              label={spec.label}
              multiline rows={3}
              size="small" fullWidth
              required={spec.required}
              error={!!err}
              helperText={err ? 'Mínimo 30 caracteres' : ''}
              sx={inputSx}
            />
          )} />
        </Box>
      );

    case 'select':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <FormControl size="small" fullWidth error={!!err} required={spec.required} sx={inputSx}>
              <InputLabel>{spec.label}</InputLabel>
              <Select {...field} label={spec.label} MenuProps={menuProps}>
                <MenuItem value=""><em>Seleccionar…</em></MenuItem>
                {spec.options?.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
              {err && <FormHelperText>Campo requerido</FormHelperText>}
            </FormControl>
          )} />
        </Box>
      );

    case 'multiselect':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue={[]} render={({ field }) => {
            const selected = field.value || [];
            const toggle = (opt) => {
              const next = selected.includes(opt)
                ? selected.filter(v => v !== opt)
                : [...selected, opt];
              field.onChange(next);
            };
            return (
              <FormControl fullWidth error={!!err} required={spec.required}>
                <FormLabel sx={{ fontSize: '0.8rem', color: '#163244', mb: 1, fontWeight: 600 }}>
                  {spec.label}{spec.required ? ' *' : ''}
                  {selected.length > 0 && (
                    <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#E7A432', fontWeight: 700 }}>
                      ({selected.length} seleccionado{selected.length > 1 ? 's' : ''})
                    </Typography>
                  )}
                </FormLabel>
                <Box sx={{
                  display: 'flex', flexWrap: 'wrap', gap: 1,
                  border: err ? '1px solid #d32f2f' : '1px solid #cddae4',
                  borderRadius: 2, p: 1.5, background: '#fafafa',
                }}>
                  {spec.options?.map(o => {
                    const checked = selected.includes(o);
                    return (
                      <Box
                        key={o}
                        onClick={() => toggle(o)}
                        sx={{
                          px: 1.5, py: 0.5, borderRadius: 20, cursor: 'pointer',
                          fontSize: '0.82rem', fontWeight: checked ? 700 : 400,
                          border: `1.5px solid ${checked ? '#163244' : '#cddae4'}`,
                          background: checked ? '#163244' : '#fff',
                          color: checked ? '#E7A432' : '#5c7a8a',
                          transition: 'all 0.15s',
                          userSelect: 'none',
                          '&:hover': { borderColor: '#163244', background: checked ? '#163244' : '#f0f5f8' },
                        }}
                      >
                        {checked ? '✓ ' : ''}{o}
                      </Box>
                    );
                  })}
                </Box>
                {err && <FormHelperText>Seleccioná al menos una opción</FormHelperText>}
                {selected.length > 0 && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#8fa3b1', mt: 0.5 }}>
                    Seleccionados: {selected.join(', ')}
                  </Typography>
                )}
              </FormControl>
            );
          }} />
        </Box>
      );

    case 'radio':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <FormControl component="fieldset" error={!!err} required={spec.required} fullWidth>
              <FormLabel sx={{ fontSize: '0.8rem', color: '#163244 !important', mb: 0.5, fontWeight: 600 }}>
                {spec.label}
              </FormLabel>
              <RadioGroup {...field} row sx={{ gap: 1 }}>
                {spec.options?.map(o => (
                  <FormControlLabel key={o} value={o}
                    control={<Radio size="small" sx={{ color: '#163244', '&.Mui-checked': { color: '#163244' } }} />}
                    label={<Typography variant="body2" sx={{ color: '#163244' }}>{o}</Typography>} />
                ))}
              </RadioGroup>
              {err && <FormHelperText>Seleccioná una opción</FormHelperText>}
            </FormControl>
          )} />
        </Box>
      );

    case 'checkbox':
      return (
        <Controller name={spec.key} control={control} defaultValue={spec.defaultValue ?? false} render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={!!field.value} size="small" />}
            label={<Typography variant="body2">{spec.label}{spec.required ? ' *' : ''}</Typography>}
          />
        )} />
      );

    case 'timeslots':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <TextField {...field} label={spec.label}
              placeholder="Ej: Lun–Vie 9–12, Sáb 14–18"
              size="small" fullWidth sx={inputSx} />
          )} />
        </Box>
      );

    case 'files':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue={null} render={({ field: { onChange } }) => (
            <Box>
              <FormLabel sx={{ display: 'block', mb: 0.5, fontSize: '0.8rem', color: '#163244' }}>
                {spec.label}
              </FormLabel>
              <input type="file" multiple accept="image/*"
                onChange={(e) => onChange(e.target.files)}
                style={{ fontSize: '0.85rem' }} />
            </Box>
          )} />
        </Box>
      );

    default:
      return null;
  }
}

// Layout: flexbox wrapping, cada campo controla su propio ancho
function FieldGrid({ specs, control, errors }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
      {specs.filter(s => s.type !== 'checkbox').map(spec => (
        <Field key={spec.key} spec={spec} control={control} errors={errors} />
      ))}
    </Box>
  );
}

export default function DynamicForm({ serviceKey, onSubmitSuccess }) {
  // ─── Estado para campos desde la API ────────────────────────────────────────
  const [apiSpecs, setApiSpecs]     = React.useState(null);  // null = cargando, [] = vacío
  const [apiLoading, setApiLoading] = React.useState(true);

  // Intenta cargar los campos desde la API — fallback a formsConfig.js si falla o está vacío
  React.useEffect(() => {
    if (!serviceKey) { setApiLoading(false); return; }
    setApiLoading(true);
    api.get(`/form-fields/${serviceKey}?t=${Date.now()}`)
      .then(({ data }) => {
        if (data.fields && data.fields.length > 0) {
          setApiSpecs(data.fields.map(dbFieldToSpec));
        } else {
          setApiSpecs(null); // fallback a formsConfig
        }
      })
      .catch(() => {
        setApiSpecs(null); // fallback a formsConfig
      })
      .finally(() => setApiLoading(false));
  }, [serviceKey]);

  // Specs finales: API si cargó, formsConfig como fallback
  const fallbackSpecs = React.useMemo(() => getFormSpecs(serviceKey), [serviceKey]);
  // Las opciones ocultas se sacan acá, después de elegir la fuente, así cubre
  // tanto los campos de la base como el fallback de formsConfig.
  const specs = React.useMemo(
    () => (apiSpecs ?? fallbackSpecs).map(s => ({ ...s, options: sinOcultas(s.key, s.options) })),
    [apiSpecs, fallbackSpecs]
  );

  const schema = React.useMemo(() => z.preprocess(vaciosComoAusentes, buildSchema(serviceKey)), [serviceKey]);

  const { control, handleSubmit, formState: { errors, isSubmitting }, reset, setValue, watch } = useForm({
    resolver: zodResolver(schema),
  });

  const [submitError, setSubmitError] = React.useState(null);
  const [submitted, setSubmitted]     = React.useState(false);
  const [geoLoading, setGeoLoading]   = React.useState(false);
  const [geoError,   setGeoError]     = React.useState('');

  // Detectar si el formulario tiene campos de ubicación
  const hasUbicacion = specs.some(f => f.key.startsWith('ubicacion.'));

  const handleGeolocate = async () => {
    setGeoError('');
    if (!navigator.geolocation) {
      setGeoError('Tu dispositivo no soporta geolocalización.');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
            { headers: { 'Accept-Language': 'es' } }
          );
          const data = await res.json();
          const addr = data.address || {};

          const direccion = [
            addr.road || addr.pedestrian || addr.footway || '',
            addr.house_number || '',
          ].filter(Boolean).join(' ') || data.display_name?.split(',')[0] || '';

          const ciudad = addr.city || addr.town || addr.village || addr.municipality || '';
          const provincia = addr.state || addr.region || '';

          if (direccion) setValue('ubicacion.direccion', direccion);
          if (ciudad)    setValue('ubicacion.ciudad',    ciudad);
          if (provincia) setValue('ubicacion.provincia', provincia);

          if (!direccion && !ciudad) {
            setGeoError('No se pudo obtener la dirección. Completala manualmente.');
          }
        } catch {
          setGeoError('Error al obtener la dirección. Intentá de nuevo.');
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) setGeoError('Permiso de ubicación denegado. Completá la dirección manualmente.');
        else setGeoError('No se pudo obtener tu ubicación. Completá la dirección manualmente.');
      },
      { timeout: 8000 }
    );
  };

  const specificSpecs   = specs.filter(f => !COMMON_KEYS.includes(f.key) && !f.key.startsWith('ubicacion.'));
  const mainCommonSpecs = specs.filter(f =>
    (COMMON_KEYS.includes(f.key) || f.key.startsWith('ubicacion.')) &&
    f.type !== 'checkbox' &&
    f.key !== 'horarios_preferidos'  // se maneja inline después de urgencia
  );
  const checkboxSpecs   = specs.filter(f => COMMON_KEYS.includes(f.key) && f.type === 'checkbox');

  // Observar urgencia para mostrar/ocultar AvailabilityPicker y derivar request_type
  const urgenciaValue = watch('urgencia', '');
  const mostrarHorarios = urgenciaValue === 'Elegir días y horarios';
  const requestType = urgenciaValue === 'Urgente (48h)' ? 'urgent' : 'quote';

  const onSubmit = async (raw) => {
    setSubmitError(null);
    const common = {};
    const serviceSpecific = {};
    specs.forEach(f => {
      // Los ubicacion.* llegan anidados en raw.ubicacion (react-hook-form guarda
      // name="ubicacion.ciudad" como { ubicacion: { ciudad } }): raw['ubicacion.ciudad']
      // era siempre undefined y la ubicación se descartaba entera — la solicitud
      // le llegaba al prestador sin ningún dato de dónde es el trabajo.
      if (f.key.startsWith('ubicacion.')) return;
      const val = raw[f.key];
      if (val === undefined) return;
      if (COMMON_KEYS.includes(f.key)) common[f.key] = val;
      else serviceSpecific[f.key] = val;
    });
    // Mismo criterio que el preprocess: un campo vacío no se guarda.
    const ubicacion = vaciosComoAusentes(raw.ubicacion);
    if (ubicacion && Object.keys(ubicacion).length > 0) common.ubicacion = ubicacion;
    // horarios_preferidos se maneja fuera de specs (campo inactivo en BD, controlado inline)
    if (mostrarHorarios && raw.horarios_preferidos) {
      common.horarios_preferidos = raw.horarios_preferidos;
    } else {
      common.horarios_preferidos = null;
    }

    // Copiar provincia y ciudad al nivel raíz de common_data
    // para que el dashboard y los reportes puedan filtrar fácilmente
    if (common.ubicacion?.provincia) common.provincia = common.ubicacion.provincia;
    if (common.ubicacion?.ciudad)    common.ciudad    = common.ubicacion.ciudad;
    try {
      const { data } = await api.post('/api/service-requests',
        { serviceKey, common, serviceSpecific, request_type: requestType }
      );
      if (onSubmitSuccess) {
        await onSubmitSuccess(data.id);
      } else {
        setSubmitted(true);
        reset();
      }
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Ocurrió un error al enviar la solicitud.');
    }
  };

  // Pantalla de carga mientras se obtienen los campos de la API
  if (apiLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#163244' }} />
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h5" sx={{ color: '#163244', mb: 1 }}>Solicitud enviada</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Nos pondremos en contacto pronto con prestadores disponibles.
        </Typography>
        <Button variant="contained"
          sx={{ backgroundColor: '#E7A432', color: '#163244' }}
          onClick={() => setSubmitted(false)}>
          Hacer otra solicitud
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {submitError && <Alert severity="error" sx={{ mb: 3 }}>{submitError}</Alert>}

      {specificSpecs.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#163244', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.72rem' }}>
            Detalles del servicio
          </Typography>
          <FieldGrid specs={specificSpecs} control={control} errors={errors} />
          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: '#163244', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.72rem' }}>
          Información general
        </Typography>

        {geoError && (
          <Alert severity="warning" sx={{ mb: 2, py: 0.5 }} onClose={() => setGeoError('')}>
            {geoError}
          </Alert>
        )}

        {/* Campos comunes — botón ubicación se inyecta inline después de provincia */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
          {mainCommonSpecs.filter(s => s.type !== 'checkbox').map(spec => (
            <React.Fragment key={spec.key}>
              <Field spec={spec} control={control} errors={errors} />

              {/* Botón ubicación — aparece justo después del campo provincia */}
              {spec.key === 'ubicacion.provincia' && hasUbicacion && (
                <Box sx={{ width: '100%', mt: -0.5, mb: 0.5 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={handleGeolocate}
                    disabled={geoLoading}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 1,
                      bgcolor: 'rgba(22,50,68,0.05)',
                      border: '1px dashed rgba(22,50,68,0.25)',
                      borderRadius: '10px', px: 2, py: 1,
                      fontSize: 12, fontWeight: 600, color: '#163244',
                      cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
                      transition: 'all 0.15s',
                      '&:hover': {
                        bgcolor: 'rgba(231,164,50,0.08)',
                        borderColor: '#E7A432',
                        color: '#b8820d',
                      },
                      '&:disabled': { opacity: 0.6, cursor: 'default' },
                    }}
                  >
                    {geoLoading
                      ? <CircularProgress size={13} sx={{ color: '#163244' }} />
                      : (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                      )
                    }
                    {geoLoading ? 'Obteniendo ubicación...' : 'Detectar mi ubicación automáticamente'}
                  </Box>
                </Box>
              )}

              {spec.key === 'urgencia' && mostrarHorarios && (
                <Box sx={{ width: '100%' }}>
                  <Controller
                    name="horarios_preferidos"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                      <AvailabilityPicker
                        value={field.value}
                        onChange={field.onChange}
                        label="Días y horarios preferidos"
                        error={!!errors.horarios_preferidos}
                      />
                    )}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {checkboxSpecs.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {checkboxSpecs.map(spec => (
            <Field key={spec.key} spec={spec} control={control} errors={errors} />
          ))}
        </Box>
      )}

      <Box
        component="button"
        type="submit"
        disabled={isSubmitting}
        sx={{
          width: '100%', mt: 4,
          bgcolor: '#163244', color: '#fff', border: 'none',
          borderRadius: '50px', py: 1.5,
          fontSize: 14, fontWeight: 700,
          cursor: isSubmitting ? 'default' : 'pointer',
          fontFamily: 'Montserrat, sans-serif',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          transition: 'background 0.15s',
          '&:hover': { bgcolor: '#1e4a6e' },
          '&:disabled': { opacity: 0.7 },
        }}
      >
        {isSubmitting
          ? <><CircularProgress size={18} sx={{ color: '#fff' }} /> Enviando...</>
          : 'Enviar solicitud →'
        }
      </Box>
    </Box>
  );
}

export { DynamicForm };
